/**
 * Photo Actions Dropdown Menu
 *
 * Provides a clean dropdown menu for photo actions including:
 * - Report (for non-owners)
 * - Delete (for owners)
 * - Moderate (for moderators)
 * - Reverse image search tools (for moderators)
 *
 * Uses the same stacking context solution as user dropdown to ensure
 * proper z-index behavior above all page content.
 */

// Helper to get translation (falls back to key if i18n not available)
function t(key) {
    return window.t ? window.t(key) : key;
}

document.addEventListener('DOMContentLoaded', function() {
    /**
     * Initialize photo actions dropdown
     */
    function initializePhotoActionsDropdown() {
        const toggleButton = document.querySelector('.photo-actions-toggle');
        const actionsDataElement = document.getElementById('photo-actions-data');
        
        if (!toggleButton || !actionsDataElement) {
            return; // Not on a photo page or no dropdown needed
        }
        
        // Parse action data from JSON script tag
        let actionData;
        try {
            actionData = JSON.parse(actionsDataElement.textContent);
        } catch (e) {
            console.error('Photo actions dropdown: Failed to parse configuration', e);
            // Fail gracefully - hide the button since we can't show the menu
            toggleButton.style.display = 'none';
            return;
        }
        
        // Only show dropdown if user is owner or moderator
        if (!actionData.isOwner && !actionData.isModerator) {
            return; // No dropdown needed for regular users viewing others' photos
        }
        
        // Create dropdown menu
        const dropdownMenu = createPhotoActionsDropdown(actionData);
        document.body.appendChild(dropdownMenu);
        
        // Setup interactions
        setupDropdownInteractions(toggleButton, dropdownMenu);
    }
    
    /**
     * Create the dropdown menu HTML
     * @param {Object} actionData - Configuration data for actions
     * @returns {HTMLElement} The dropdown menu element
     */
    function createPhotoActionsDropdown(actionData) {
        const dropdown = document.createElement('div');
        dropdown.className = 'photo-actions-dropdown';
        dropdown.style.cssText = `
            display: none;
            position: fixed;
            z-index: 999999;
            min-width: 200px;
            max-width: 280px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1), 0 6px 10px rgba(0,0,0,0.08);
            border: 1px solid #e5e7eb;
            padding: 4px 0;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
        `;
        
        let menuItems = [];
        
        // Published photos owned by someone else must go through moderation so
        // the deletion has a reason and optional owner notice.
        if (actionData.isOwner) {
            menuItems.push({
                href: actionData.deleteUrl,
                icon: 'fa-trash',
                label: t('common.photo_delete'),
                className: 'delete-action confirm',
                confirm: actionData.deleteConfirmText
            });
        }
        
        // Moderator actions
        if (actionData.isModerator) {
            if (!actionData.isOwner) {
                if (menuItems.length > 0) {
                    menuItems.push({ type: 'separator' });
                }

                menuItems.push({
                    type: 'button',
                    icon: 'fa-bell',
                    label: t('common.photo_moderate'),
                    className: 'photo-mod-action'
                });
            }

            // Reverse image search section
            if (menuItems.length > 0) {
                menuItems.push({ type: 'separator' });
            }
            menuItems.push({ type: 'header', label: t('common.photo_reverse_search') });

            menuItems.push({
                href: actionData.tineyeUrl,
                icon: 'fa-search',
                label: t('common.photo_search_tineye'),
                target: '_blank'
            });

            menuItems.push({
                href: actionData.googleUrl,
                icon: 'fa-search',
                label: t('common.photo_search_google'),
                target: '_blank'
            });

            menuItems.push({
                href: actionData.yandexUrl,
                icon: 'fa-search',
                label: t('common.photo_search_yandex'),
                target: '_blank'
            });
        }
        
        // Build menu HTML
        let html = '';
        menuItems.forEach(item => {
            if (item.type === 'separator') {
                html += '<div class="border-t border-gray-200 my-1"></div>';
            } else if (item.type === 'header') {
                html += `<div class="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">${item.label}</div>`;
            } else {
                html += createMenuItemHTML(item);
            }
        });
        
        dropdown.innerHTML = html;
        return dropdown;
    }
    
    /**
     * Create HTML for a single menu item
     * @param {Object} item - Menu item configuration
     * @returns {string} HTML string for the menu item
     */
    function createMenuItemHTML(item) {
        const baseClasses = `flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors ${item.className || ''}`;

        // Use button element for non-navigation actions
        if (item.type === 'button') {
            return `
                <button type="button" class="${baseClasses} w-full text-left">
                    <i class="fa ${item.icon} w-5 text-gray-500 mr-2"></i>
                    <span>${item.label}</span>
                </button>
            `;
        }

        // Use anchor element for navigation actions
        const targetAttr = item.target ? `target="${item.target}"` : '';
        const dataAttr = item.dataTargetId ? `data-targetid="${item.dataTargetId}"` : '';
        const confirmAttr = item.confirm ? `conf="${item.confirm}"` : '';

        return `
            <a href="${item.href}"
               class="${baseClasses}"
               ${targetAttr}
               ${dataAttr}
               ${confirmAttr}>
                <i class="fa ${item.icon} w-5 text-gray-500 mr-2"></i>
                <span>${item.label}</span>
                ${item.target ? '<i class="fa fa-external-link text-xs text-gray-400 ml-auto"></i>' : ''}
            </a>
        `;
    }
    
    /**
     * Setup dropdown interactions
     * @param {HTMLElement} toggleButton - The dropdown toggle button
     * @param {HTMLElement} dropdownMenu - The dropdown menu element
     */
    function setupDropdownInteractions(toggleButton, dropdownMenu) {
        let isPositioned = false;
        
        /**
         * Position the dropdown relative to the toggle button
         */
        function positionDropdown() {
            if (isPositioned) return;
            
            const buttonRect = toggleButton.getBoundingClientRect();
            const menuWidth = dropdownMenu.offsetWidth;
            
            // Position below and to the left of the button (since button is in top-right)
            dropdownMenu.style.top = (buttonRect.bottom + 4) + 'px';
            dropdownMenu.style.left = (buttonRect.right - menuWidth) + 'px';
            
            // Ensure dropdown doesn't go off the right edge of screen
            const dropdownRect = dropdownMenu.getBoundingClientRect();
            if (dropdownRect.right > window.innerWidth - 8) {
                dropdownMenu.style.left = (window.innerWidth - menuWidth - 8) + 'px';
            }
            
            // Ensure dropdown doesn't go off the left edge
            if (dropdownRect.left < 8) {
                dropdownMenu.style.left = '8px';
            }
            
            isPositioned = true;
        }
        
        /**
         * Show the dropdown menu
         */
        function showDropdown() {
            isPositioned = false;
            dropdownMenu.style.display = 'block';
            
            // Force reflow before adding visibility
            dropdownMenu.offsetHeight;
            
            dropdownMenu.style.visibility = 'visible';
            dropdownMenu.style.opacity = '1';
            toggleButton.setAttribute('aria-expanded', 'true');
            toggleButton.classList.add('bg-gray-100');
            
            positionDropdown();
        }
        
        /**
         * Hide the dropdown menu
         */
        function hideDropdown() {
            dropdownMenu.style.visibility = 'hidden';
            dropdownMenu.style.opacity = '0';
            toggleButton.setAttribute('aria-expanded', 'false');
            toggleButton.classList.remove('bg-gray-100');
            
            // Hide after transition completes
            setTimeout(() => {
                if (dropdownMenu.style.opacity === '0') {
                    dropdownMenu.style.display = 'none';
                }
            }, 150);
            
            isPositioned = false;
        }
        
        /**
         * Check if dropdown is visible
         * @returns {boolean} True if dropdown is visible
         */
        function isDropdownVisible() {
            return dropdownMenu.style.display === 'block' && dropdownMenu.style.opacity === '1';
        }
        
        // Toggle dropdown on button click
        toggleButton.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            if (isDropdownVisible()) {
                hideDropdown();
            } else {
                showDropdown();
            }
        });
        
        // Close on outside click
        document.addEventListener('click', function(e) {
            if (isDropdownVisible() && 
                !toggleButton.contains(e.target) && 
                !dropdownMenu.contains(e.target)) {
                hideDropdown();
            }
        });
        
        // Keyboard navigation support
        document.addEventListener('keydown', function(e) {
            if (!isDropdownVisible()) return;
            
            if (e.key === 'Escape') {
                hideDropdown();
                toggleButton.focus();
                return;
            }
            
            // Add arrow key navigation for accessibility
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const links = dropdownMenu.querySelectorAll('a:not([style*="display: none"])');
                const currentIndex = Array.from(links).indexOf(document.activeElement);
                
                let nextIndex;
                if (e.key === 'ArrowDown') {
                    nextIndex = currentIndex + 1 >= links.length ? 0 : currentIndex + 1;
                } else {
                    nextIndex = currentIndex - 1 < 0 ? links.length - 1 : currentIndex - 1;
                }
                
                links[nextIndex]?.focus();
            }
        });
        
        // Reposition on window resize
        window.addEventListener('resize', function() {
            if (isDropdownVisible()) {
                isPositioned = false;
                positionDropdown();
            }
        });
        
        // Handle confirm dialogs for delete actions using event delegation
        // This works with dynamically created elements
        dropdownMenu.addEventListener('click', function(e) {
            const confirmLink = e.target.closest('.confirm');
            if (confirmLink) {
                e.preventDefault();
                e.stopPropagation();
                const confirmText = confirmLink.getAttribute('conf');
                if (confirmText && confirm(confirmText)) {
                    window.location.href = confirmLink.href;
                }
            }
        });
        
        // Handle moderation dialog trigger
        dropdownMenu.addEventListener('click', function(e) {
            const modLink = e.target.closest('.photo-mod-action');
            if (modLink) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                // Open Alpine.js modal
                if (window.openPhotoModModal) {
                    window.openPhotoModModal(toggleButton);
                } else {
                    // Fallback: dispatch Alpine event
                    window.dispatchEvent(new CustomEvent('open-photo-mod-modal'));
                }

                hideDropdown();
                return false;
            }
        });
    }
    
    // Initialize the dropdown
    initializePhotoActionsDropdown();
});
